import { useCallback, useMemo, useState } from 'react';
import useAnalyticsStore from '../../../../store/analyticsStore';
import { IWaveSpectraParams } from '../../../../types/waveSpectra';
import { formatRangeEnd, formatRangePartToTime } from '../../../../utils/dateRange';
import { DATASET_CATEGORIES } from '../../../../utils/filterBestDataset';
import { useIsDwf } from '../../../../utils/hooks';
import { DT0Format } from '../../../../utils/StaticVariable';
import { useTemporaryDWFSpectraBlocker } from '../../ResultPanel/hooks/useTemporaryDWFSpectraBlocker';
import { useEnrichedTask } from './shared/useEnrichedTask';
import { useWaveSpectraCoverage } from './shared/useWaveSpectraCoverage';
import { useWaveSpectraDateRange } from './shared/useWaveSpectraDateRange';

// Zarr-backed spectra datasets are a fixed list of predefined output points rather
// than a mesh with a coverage polygon, so the usual shapefile-based coverage check
// doesn't apply to them and must be bypassed.
const ZARR_SPECTRA_DATASET_IDS = ['oceanbeat-sw-nora3', 'north-europe-sw-nora3'];

const isZarrSpectraDataset = (datasetId: string) => {
  const lowerCaseDatasetId = datasetId.toLowerCase();
  return ZARR_SPECTRA_DATASET_IDS.some((id) => lowerCaseDatasetId.includes(id));
};

export const useWaveSpectraParams = () => {
  const {
    previewDatasetId,
    coordinates,
    categoryDataset,
    previewDatasetTimeRange,
    onDatasetChange,
    pointName,
  } = useAnalyticsStore((state) => state.analyticsContext);

  const isWavesDataset = categoryDataset.some(
    (dataset) => dataset.id === previewDatasetId && dataset.label === DATASET_CATEGORIES.WAVES
  );

  const isDwf = useIsDwf(previewDatasetId);

  const isZarrSpectra = isZarrSpectraDataset(previewDatasetId);

  const task = useEnrichedTask(previewDatasetId, coordinates);

  const isWaveSpectraAvailable = isWavesDataset || isDwf || task?.IsWaveSpectra === true;

  // Coverage is only meaningful once the dataset is already category-eligible;
  // isWaveSpectraAvailable stays the gate for the "wrong dataset type" message.
  // Zarr-backed spectra datasets don't have a coverage shapefile/polygon (they're
  // a fixed list of predefined output points), so the coverage check is skipped
  // for them entirely.
  const { hasCoverage } = useWaveSpectraCoverage(
    isWaveSpectraAvailable && !isZarrSpectra ? previewDatasetId : '',
    coordinates
  );
  const isSpectraCoverageMissing =
    isWaveSpectraAvailable && !isZarrSpectra && hasCoverage === false;

  const variables = useMemo(
    () => task?.ItemsInfo.filter((item) => item.Name !== 'Time') ?? [],
    [task]
  );

  const timeseriesVariable = useMemo(
    () =>
      variables.find((item) => item.ShortName === task?.DefaultValueShortName) ??
      variables[0] ??
      null,
    [variables, task]
  );

  const dateRange = useWaveSpectraDateRange(previewDatasetTimeRange);
  // TODO: REMOVE THIS HARDCODE WHEN FIXED
  const { isSpectraBlocked, spectraBlockedMessage } = useTemporaryDWFSpectraBlocker(
    previewDatasetId,
    dateRange.endDate
  );

  const [submittedParams, setSubmittedParams] = useState<IWaveSpectraParams | null>(null);

  const canUpdate = Boolean(
    isWaveSpectraAvailable &&
      !isSpectraCoverageMissing &&
      !isSpectraBlocked &&
      previewDatasetId &&
      timeseriesVariable &&
      dateRange.startDate &&
      dateRange.endDate
  );

  const onUpdate = useCallback(() => {
    if (!timeseriesVariable || !dateRange.startDate || !dateRange.endDate) {
      return;
    }

    const [longitude, latitude] = coordinates;
    setSubmittedParams({
      datasetId: previewDatasetId,
      variableIndex: timeseriesVariable.Index,
      longitude,
      latitude,
      startDate: formatRangePartToTime(dateRange.startDate),
      endDate: formatRangeEnd(dateRange.endDate),
      // Bypass to ensure always got fresh data
      orderId: `MoodV2_${crypto.randomUUID()}`,
      pointName,
    });
  }, [
    timeseriesVariable,
    dateRange.startDate,
    dateRange.endDate,
    coordinates,
    previewDatasetId,
    pointName,
  ]);

  return {
    datasets: categoryDataset,
    datasetId: previewDatasetId,
    onDatasetChange,
    isWaveSpectraAvailable,
    isSpectraCoverageMissing,
    dateRange,
    submittedParams,
    canUpdate,
    onUpdate,
    orderId: task?.OrderId ?? '',
    pointName,
    timeseriesVariable,
    isDateRangeBlocked: isSpectraBlocked,
    spectraBlockedMessage,
  };
};
